import React, { useEffect, useMemo, useState } from 'react';
import { Plus, Trash2, Repeat } from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Textarea } from '../../../components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../../components/ui/select';
import { RadioGroup, RadioGroupItem } from '../../../components/ui/radio-group';
import { toast } from 'sonner';
import { clientPortalService } from '../../../services/clientPortal.service';
import { recurringOrdersService } from '../../../services/recurringOrders.service';
import { WEEKDAY_LABELS } from './recurringShared';

const WEEKDAY_NAMES = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

const indicesFromAllowedDays = (days) => {
  if (!Array.isArray(days) || days.length === 0) return new Set();
  const lower = days.map((d) => String(d || '').toLowerCase());
  return new Set(WEEKDAY_NAMES.map((n, i) => (lower.includes(n) ? i : null)).filter((v) => v !== null));
};

const todayStr = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const priceOf = (p) => (p?.has_discount ? p.your_price : p?.wholesale_price ?? p?.your_price ?? 0);

// Create/edit dialog for a recurring order schedule.
export const ScheduleFormDialog = ({ open, onOpenChange, template, prefill, locations, allowedDays, onSaved }) => {
  const isEdit = !!template;
  const [products, setProducts] = useState([]);
  const [saving, setSaving] = useState(false);

  const [mask, setMask] = useState([]);
  const [items, setItems] = useState([]);
  const [locationId, setLocationId] = useState('');
  const [startDate, setStartDate] = useState(todayStr());
  const [endDate, setEndDate] = useState('');
  const [poNumber, setPoNumber] = useState('');
  const [notes, setNotes] = useState('');
  const [noActionPolicy, setNoActionPolicy] = useState('');
  const [menuChangePolicy, setMenuChangePolicy] = useState('skip');
  const [addProductId, setAddProductId] = useState('');

  useEffect(() => {
    if (!open) return;
    clientPortalService.getProducts().then(setProducts).catch(() => {
      toast.error('Failed to load the product catalog');
    });
    if (template) {
      setMask(template.weekday_mask || []);
      setItems((template.items || []).map((i) => ({ product_id: i.product_id, quantity: i.quantity })));
      setLocationId(template.delivery_location_id || '');
      setStartDate(template.start_date || todayStr());
      setEndDate(template.end_date || '');
      setPoNumber(template.po_number || '');
      setNotes(template.notes || '');
      setNoActionPolicy(template.no_action_policy || '');
      setMenuChangePolicy(template.menu_change_policy || 'skip');
    } else {
      setMask([]);
      setItems(prefill?.items || []);
      setLocationId(prefill?.location_id || locations?.[0]?.id || '');
      setStartDate(todayStr());
      setEndDate('');
      setPoNumber('');
      setNotes('');
      setNoActionPolicy('');
      setMenuChangePolicy('skip');
    }
    setAddProductId('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, template]);

  const productMap = useMemo(() => Object.fromEntries(products.map((p) => [p.id, p])), [products]);
  const availableToAdd = products.filter((p) => !items.some((i) => i.product_id === p.id));

  // Which weekdays this account is actually allowed to receive on. Recurring
  // schedules must be a subset of these — the backend also validates. If the
  // account has none configured, the whole cadence picker is locked with a
  // helpful message.
  const allowedIndices = useMemo(() => indicesFromAllowedDays(allowedDays), [allowedDays]);
  const clientHasAnyDay = allowedIndices.size > 0;
  const allSevenAllowed = allowedIndices.size === 7;
  const allowedLabels = useMemo(
    () => Array.from(allowedIndices).sort((a, b) => a - b).map((i) => WEEKDAY_LABELS[i]),
    [allowedIndices],
  );
  const isDaily = clientHasAnyDay
    && mask.length === allowedIndices.size
    && mask.every((d) => allowedIndices.has(d));

  const toggleDay = (d) => {
    if (!allowedIndices.has(d)) return;
    setMask((m) => (m.includes(d) ? m.filter((x) => x !== d) : [...m, d].sort()));
  };

  const clickDaily = () => {
    if (!clientHasAnyDay) return;
    setMask(isDaily ? [] : Array.from(allowedIndices).sort((a, b) => a - b));
  };

  const addItem = () => {
    if (!addProductId) return;
    setItems((cur) => [...cur, { product_id: addProductId, quantity: 1 }]);
    setAddProductId('');
  };

  const setQty = (pid, qty) =>
    setItems((cur) => cur.map((i) => (i.product_id === pid ? { ...i, quantity: qty } : i)));

  const estTotal = items.reduce(
    (sum, i) => sum + priceOf(productMap[i.product_id]) * (i.quantity || 0), 0
  );

  const handleSave = async () => {
    if (mask.length === 0) return toast.error('Pick at least one delivery weekday.');
    if (items.length === 0) return toast.error('Add at least one product.');
    if (items.some((i) => !i.quantity || i.quantity < 1)) return toast.error('Every item needs a quantity of at least 1.');
    if (!locationId) return toast.error('Pick a delivery location.');
    if (!noActionPolicy) return toast.error("Choose what should happen if you don't act by a deadline.");

    const payload = {
      delivery_location_id: locationId,
      weekday_mask: mask,
      items: items.map((i) => ({ product_id: i.product_id, quantity: i.quantity })),
      start_date: startDate,
      end_date: endDate || null,
      no_action_policy: noActionPolicy,
      menu_change_policy: menuChangePolicy,
      po_number: poNumber || null,
      notes: notes || null,
    };
    setSaving(true);
    try {
      if (isEdit) {
        await recurringOrdersService.updateTemplate(template.id, payload);
        toast.success('Schedule updated');
      } else {
        await recurringOrdersService.createTemplate(payload);
        toast.success('Recurring order schedule created');
      }
      onOpenChange(false);
      onSaved?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save schedule');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="schedule-form-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Repeat className="h-5 w-5 text-amber-500" />
            {isEdit ? 'Edit recurring schedule' : 'Create recurring schedule'}
          </DialogTitle>
          <DialogDescription>
            Orders are only placed after you confirm each date — or automatically, if you choose.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5">
          {/* Cadence */}
          <div>
            <Label className="mb-2 block">Delivery days</Label>
            {!clientHasAnyDay && (
              <div
                className="mb-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900"
                data-testid="recurring-no-delivery-days-warning"
              >
                Your account has no allowed delivery days configured. Please contact MAD Donuts
                to set up delivery days before creating a recurring schedule.
              </div>
            )}
            {clientHasAnyDay && (
              <div className="text-xs text-gray-600 mb-2" data-testid="recurring-allowed-days-hint">
                Your account accepts deliveries on: <span className="font-medium">
                  {allSevenAllowed ? 'every day of the week' : allowedLabels.join(', ')}
                </span>. Other days are disabled.
              </div>
            )}
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={clickDaily}
                disabled={!clientHasAnyDay}
                className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${
                  isDaily ? 'bg-amber-500 text-white border-amber-500' : 'bg-white text-gray-600 border-gray-300 hover:border-amber-400'
                } ${!clientHasAnyDay ? 'opacity-40 cursor-not-allowed hover:border-gray-300' : ''}`}
                title={!allSevenAllowed && clientHasAnyDay
                  ? 'Selects every day your account is allowed to receive on.'
                  : undefined}
                data-testid="weekday-chip-daily"
              >
                {allSevenAllowed || !clientHasAnyDay ? 'Daily' : 'All allowed days'}
              </button>
              {WEEKDAY_LABELS.map((lbl, d) => {
                const isAllowed = allowedIndices.has(d);
                return (
                  <button
                    key={lbl}
                    type="button"
                    onClick={() => toggleDay(d)}
                    disabled={!isAllowed}
                    title={isAllowed ? undefined : `${lbl} is not an allowed delivery day for your account`}
                    className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${
                      mask.includes(d) ? 'bg-amber-500 text-white border-amber-500' : 'bg-white text-gray-600 border-gray-300 hover:border-amber-400'
                    } ${!isAllowed ? 'opacity-40 line-through cursor-not-allowed hover:border-gray-300' : ''}`}
                    data-testid={`weekday-chip-${lbl.toLowerCase()}`}
                  >
                    {lbl}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Items */}
          <div>
            <Label className="mb-2 block">Items</Label>
            <div className="space-y-2">
              {items.map((i) => {
                const p = productMap[i.product_id];
                return (
                  <div key={i.product_id} className="flex items-center gap-3 p-2 border rounded-lg" data-testid={`schedule-item-${i.product_id}`}>
                    {p?.image_url && <img src={p.image_url} alt="" className="h-10 w-10 rounded object-cover" />}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">{p?.name || i.product_id}</p>
                      {p && (
                        <p className="text-xs text-gray-500">
                          {p.has_discount ? (
                            <>
                              <span className="line-through mr-1">${p.wholesale_price.toFixed(2)}</span>
                              <span className="text-green-600 font-medium">${p.your_price.toFixed(2)}</span>
                            </>
                          ) : (
                            <>${(p.wholesale_price ?? 0).toFixed(2)}</>
                          )}{' '}/ {p.pack_size}
                        </p>
                      )}
                    </div>
                    <Input
                      type="number"
                      min={1}
                      value={i.quantity}
                      onChange={(e) => setQty(i.product_id, parseInt(e.target.value, 10) || 0)}
                      className="w-20"
                      data-testid={`schedule-item-qty-${i.product_id}`}
                    />
                    <Button
                      variant="ghost" size="icon"
                      onClick={() => setItems((cur) => cur.filter((x) => x.product_id !== i.product_id))}
                      data-testid={`schedule-item-remove-${i.product_id}`}
                    >
                      <Trash2 className="h-4 w-4 text-gray-400" />
                    </Button>
                  </div>
                );
              })}
              <div className="flex gap-2">
                <Select value={addProductId} onValueChange={setAddProductId}>
                  <SelectTrigger className="flex-1" data-testid="schedule-add-product-select">
                    <SelectValue placeholder="Add a product…" />
                  </SelectTrigger>
                  <SelectContent>
                    {availableToAdd.map((p) => (
                      <SelectItem key={p.id} value={p.id}>
                        {p.name} — ${priceOf(p).toFixed(2)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button variant="outline" onClick={addItem} disabled={!addProductId} data-testid="schedule-add-product-btn">
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
            </div>
            {items.length > 0 && (
              <p className="text-xs text-gray-500 mt-2" data-testid="schedule-est-total">
                Estimated total per delivery: <span className="font-semibold text-gray-700">${estTotal.toFixed(2)}</span>
                <span className="ml-1">(prices may vary by date)</span>
              </p>
            )}
          </div>

          {/* Location + dates */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label className="mb-2 block">Delivery location</Label>
              <Select value={locationId} onValueChange={setLocationId}>
                <SelectTrigger data-testid="schedule-location-select">
                  <SelectValue placeholder="Choose a location" />
                </SelectTrigger>
                <SelectContent>
                  {(locations || []).map((l) => (
                    <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="mb-2 block">PO number (optional)</Label>
              <Input value={poNumber} onChange={(e) => setPoNumber(e.target.value)} data-testid="schedule-po-input" />
            </div>
            <div>
              <Label className="mb-2 block">Start date</Label>
              <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} data-testid="schedule-start-date" />
            </div>
            <div>
              <Label className="mb-2 block">End date (optional)</Label>
              <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} data-testid="schedule-end-date" />
            </div>
          </div>

          {/* Policies */}
          <div>
            <Label className="mb-1 block">If I don't act by a deadline…</Label>
            <p className="text-xs text-gray-500 mb-2">
              Each date has an ordering deadline. If you haven't confirmed or skipped by the morning of the
              deadline, this policy decides what happens.
            </p>
            <RadioGroup value={noActionPolicy} onValueChange={setNoActionPolicy} data-testid="no-action-policy-group">
              <label className="flex items-start gap-3 p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
                <RadioGroupItem value="auto_place" data-testid="policy-auto-place" />
                <div>
                  <p className="text-sm font-medium text-gray-900">Place the order automatically</p>
                  <p className="text-xs text-gray-500">Your standing order goes in as-is at the deadline.</p>
                </div>
              </label>
              <label className="flex items-start gap-3 p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
                <RadioGroupItem value="auto_skip" data-testid="policy-auto-skip" />
                <div>
                  <p className="text-sm font-medium text-gray-900">Skip that delivery</p>
                  <p className="text-xs text-gray-500">Nothing is ordered unless you confirm the date yourself.</p>
                </div>
              </label>
            </RadioGroup>
          </div>

          {noActionPolicy === 'auto_place' && (
            <div>
              <Label className="mb-1 block">If the menu changed and items are unavailable…</Label>
              <RadioGroup value={menuChangePolicy} onValueChange={setMenuChangePolicy} data-testid="menu-change-policy-group">
                <label className="flex items-start gap-3 p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
                  <RadioGroupItem value="skip" data-testid="menu-policy-skip" />
                  <div>
                    <p className="text-sm font-medium text-gray-900">Skip this delivery</p>
                    <p className="text-xs text-gray-500">Safest — nothing ships without your review.</p>
                  </div>
                </label>
                <label className="flex items-start gap-3 p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
                  <RadioGroupItem value="place_available" data-testid="menu-policy-place-available" />
                  <div>
                    <p className="text-sm font-medium text-gray-900">Place the available items only</p>
                    <p className="text-xs text-gray-500">Unavailable lines are dropped and listed in the confirmation.</p>
                  </div>
                </label>
              </RadioGroup>
            </div>
          )}

          <div>
            <Label className="mb-2 block">Notes (optional)</Label>
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} data-testid="schedule-notes-input" />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="schedule-cancel-btn">Cancel</Button>
          <Button className="bg-amber-500 hover:bg-amber-600" onClick={handleSave} disabled={saving} data-testid="schedule-save-btn">
            {saving ? 'Saving…' : isEdit ? 'Save changes' : 'Create schedule'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
